import { LEGAL } from '@affily/shared/constants';

/**
 * Shared frame for the Terms and Privacy pages: title, "last updated", a short
 * plain-English summary, then sections. Keeping it in one place means the two
 * documents cannot drift apart in layout, and the entity name, contact address
 * and date come from a single constants block (LEGAL in
 * shared/src/constants.js).
 */

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatLegalDate(iso = LEGAL.lastUpdated) {
  return dateFormatter.format(new Date(`${iso}T00:00:00Z`));
}

export function ContactLink({ className = 'text-accent underline underline-offset-2' }) {
  return (
    <a href={`mailto:${LEGAL.contactEmail}`} className={className}>
      {LEGAL.contactEmail}
    </a>
  );
}

export function LegalPage({ title, intro, summary, children }) {
  return (
    <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
      <h1 className="text-4xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-3 text-sm text-ink-faint">
        Last updated: <time dateTime={LEGAL.lastUpdated}>{formatLegalDate()}</time>
      </p>
      {intro && <p className="mt-4 text-lg text-ink-muted">{intro}</p>}

      <aside className="mt-8 rounded-lg border border-rule bg-paper-raised p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-faint">
          In plain English
        </h2>
        <ul className="mt-3 list-disc space-y-1.5 pl-5 text-ink-muted">
          {summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-ink-faint">
          This summary is a convenience. The sections below are the actual terms.
        </p>
      </aside>

      {children}
    </article>
  );
}

export function LegalSection({ title, id, children }) {
  return (
    <section id={id} className="mt-12 scroll-mt-24">
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-ink-muted">{children}</div>
    </section>
  );
}

export function LegalList({ children }) {
  return <ul className="list-disc space-y-1.5 pl-5">{children}</ul>;
}
